import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { RouteContext } from '../../router.jsx';
import { useApi } from '../../hooks/useApi.js';

const categoryOptions = [
  { value: '', label: 'Select category' },
  { value: 'cat1', label: 'Category 1' },
  { value: 'cat2', label: 'Category 2' },
  { value: 'cat3', label: 'Category 3' },
  { value: 'cat4', label: 'Category 4' }
];

export default function StudentRequestNew() {
  const { navigate } = React.useContext(RouteContext);
  const { data: plan, loading, error } = useApi('/api/students/me');
  const [form, setForm] = React.useState({ category: '', removeCourse: '', addCourse: '' });
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });

  const removeOptions = React.useMemo(() => {
    if (!plan) return [];
    switch (form.category) {
      case 'cat1':
        return filterCourses([plan.cat1]);
      case 'cat2':
        return filterCourses([plan.cat2]);
      case 'cat3':
        return filterCourses([plan.cat31, plan.cat32]);
      case 'cat4':
        return filterCourses([plan.cat41, plan.cat42]);
      default:
        return [];
    }
  }, [plan, form.category]);

  React.useEffect(() => {
    if (removeOptions.length === 0) {
      setForm((prev) => ({ ...prev, removeCourse: '' }));
      return;
    }
    if (!removeOptions.includes(form.removeCourse)) {
      setForm((prev) => ({ ...prev, removeCourse: removeOptions[0] }));
    }
  }, [removeOptions]);

  const updateField = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.category) return 'Select a category.';
    if (!form.removeCourse) return 'Select a course to remove.';
    return '';
  };

  const handleSubmit = async () => {
    const validation = validate();
    if (validation) {
      setMessage({ text: validation, tone: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch('/api/requests', {
        method: 'POST',
        body: JSON.stringify({
          remove_course: form.removeCourse,
          add_course: form.addCourse,
          category: form.category
        })
      });
      navigate('/student/requests');
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to submit request.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Submit Course Change</h1>
          <p className="subtle">Requests are reviewed by your advisor and the department chair.</p>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      <Card title="Request Details">
        {loading ? (
          <p className="subtle">Loading study plan...</p>
        ) : (
          <div className="form-grid">
            <label className="input-field">
              <span className="input-label">Category</span>
              <select className="input" value={form.category} onChange={updateField('category')}>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <span className="input-helper">Category 1 & 2 require one course. Category 3 & 4 require two courses.</span>
            </label>

            <label className="input-field">
              <span className="input-label">Remove Course</span>
              <select
                className="input"
                value={form.removeCourse}
                onChange={updateField('removeCourse')}
                disabled={!form.category || removeOptions.length === 0}
              >
                <option value="">Select course</option>
                {removeOptions.map((course) => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
              {form.category && removeOptions.length === 0 ? (
                <span className="input-helper">No course found in this category on your plan.</span>
              ) : null}
            </label>

            <Input label="Add Course" value={form.addCourse} onChange={updateField('addCourse')} placeholder="EE 5310" />
          </div>
        )}
        <div className="form-actions">
          <Button variant="secondary" type="button" onClick={() => navigate('/student/requests')}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} disabled={saving || loading}>{saving ? 'Submitting…' : 'Submit Request'}</Button>
        </div>
      </Card>
    </div>
  );
}

function filterCourses(list) {
  return list
    .map((item) => (item || '').trim())
    .filter(Boolean);
}
